import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Icon API",
  "title": "Icon"
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DocsHeading = makeShortcode("DocsHeading");
const Alert = makeShortcode("Alert");
const SimplePropsTable = makeShortcode("SimplePropsTable");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
<DocsHeading to="/design-intro/icons/" type="api" mdxType="DocsHeading">
  Icon
</DocsHeading>
<Alert variant="warning" mdxType="Alert">
  <strong>Important!</strong> Icons are now imported from{' '}
  <inlineCode>react-magma-icons</inlineCode>
  which must be installed as a peer dependency.
</Alert>
<h2>{`Basic Usage`}</h2>
<p>{`Icons are imported from a separate `}<inlineCode parentName="p">{`react-magma-icons`}</inlineCode>{` package.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { GroupIcon } from 'react-magma-icons';
export function Example() {
  return <GroupIcon />;
}
`}</code></pre>
<h2>{`Title`}</h2>
<p>{`The `}<inlineCode parentName="p">{`title`}</inlineCode>{` prop can be used to set the `}<inlineCode parentName="p">{`<title>`}</inlineCode>{` element within the SVG.`}</p>
<p>{`The title/svg association will be handled via the passed in `}<inlineCode parentName="p">{`id`}</inlineCode>{` if one is supplied or via an auto-generated `}<inlineCode parentName="p">{`id`}</inlineCode>{` if not supplied. The `}<inlineCode parentName="p">{`id`}</inlineCode>{` of the title is linked to the SVG with `}<inlineCode parentName="p">{`aria-labelledby`}</inlineCode>{`,
so it will be read screen readers.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { CrossIcon, InfoIcon } from 'react-magma-icons';
export function Example() {
  return (
    <>
      <CrossIcon title="Close This Message" />
      <br />
      <br />
      <InfoIcon id="infoIcon" title="Info Available" />
    </>
  );
}
`}</code></pre>
<h2>{`Sizes`}</h2>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { CheckIcon } from 'react-magma-icons';
export function Example() {
  return (
    <>
      <CheckIcon size={20} title="Small Size" />
      <br />
      <br />
      <CheckIcon size={50} title="Large Size" />
    </>
  );
}
`}</code></pre>
<h2>{`Color`}</h2>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { AngleDownIcon } from 'react-magma-icons';
export function Example() {
  return (
    <>
      <AngleDownIcon title="No Color" />
      <br />
      <br />
      <AngleDownIcon color="green" title="Some Color" />
    </>
  );
}
`}</code></pre>
<h2>{`Accessibility`}</h2>
<p>{`To improve the experience for assistive technology users and remove the icon and its children from the accessibility tree, add the prop `}<inlineCode parentName="p">{`aria-hidden="true"`}</inlineCode>{` to your icon.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { HomeIcon } from 'react-magma-icons';
export function Example() {
  return <HomeIcon aria-hidden="true" />;
}
`}</code></pre>
<hr></hr>
<h2>{`Available Icons`}</h2>
<pre><code parentName="pre" {...{
        "className": "language-typescript",
        "metastring": "noCode noCodeSandbox noCopy noBorder",
        "noCode": true,
        "noCodeSandbox": true,
        "noCopy": true,
        "noBorder": true
      }}>{`import React from 'react';
import ICONS from 'react-magma-icons';

export function Example() {
  return (
    <div>
      {Object.keys(ICONS.categories)
        .sort((a, b) => {
          a = a.toLowerCase();
          b = b.toLowerCase();
          return a > b ? 1 : a < b ? -1 : 0;
        })
        .map(category => {
          return (
            <div>
              <Heading level={3}>{category}</Heading>
              <div className="demo-icon-list-container">
                {ICONS.categories[category].map(icon => {
                  const id = icon + 'Available';
                  const title = icon + ' Title';
                  const Icon = ICONS[icon];

                  return (
                    <span className="demo-icon-container" key={id}>
                      <Icon size={40} title={title} />
                      <br />
                      <span class="icon-name">{icon}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
    </div>
  );
}
`}</code></pre>
<hr></hr>
<h2>{`Testing`}</h2>
<p>{`Passing in the `}<inlineCode parentName="p">{`testId`}</inlineCode>{` prop will add the `}<inlineCode parentName="p">{`data-testid`}</inlineCode>{` attribute to the svg element for easier querying in tests.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "noCodeSandbox",
        "noCodeSandbox": true
      }}>{`<CheckIcon testId="check-icon-id" title="Check Icon" />
`}</code></pre>
<h2>{`Icon Props`}</h2>
<p><strong parentName="p">{`Any other props supplied will be provided to the `}<inlineCode parentName="strong">{`svg`}</inlineCode>{` element.`}</strong></p>
<SimplePropsTable propertyValues={{
      color: {
        type: {
          name: 'string'
        },
        required: false,
        description: 'Hex code for the color',
        defaultValue: 'inherit'
      },
      size: {
        type: {
          name: 'number'
        },
        required: false,
        description: 'Number of pixels for the icon height and width',
        defaultValue: '24'
      },
      title: {
        type: {
          name: 'string'
        },
        required: false,
        description: 'The title of the svg element, to be read by screen readers',
        defaultValue: ''
      }
    }} mdxType="SimplePropsTable" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      